import { FieldErrors, FieldValues, useFormContext, UseFormGetFieldState } from 'react-hook-form';
import { useSelector } from 'react-redux';
import {
  BlockType,
  CoApplicantFields,
  CustomerType,
  mapEntrataCustomerTypeToRelationshipType,
  RelationshipType,
  SystemTranslationKeys,
} from '@common/entities';
import { RootState } from 'state/store';

import { ConditionalElementState } from 'hooks/display-logic/conditionalElementState';
import { useEvaluateDisplayCondition } from 'hooks/display-logic/useEvaluateDisplayCondition';
import { useApplicationSettings } from 'hooks/useApplicationSettings';
import { useSystemTranslation } from 'hooks/useSystemTranslation';
import {
  getCoApplicants,
  getCustomerRelationships,
  getGuarantors,
  getResponse,
  signupSecondaryApplicant,
} from 'services';
import type { CoapplicantField, GuarantorField } from 'types/fields';
import { getBlockFormFieldNames } from 'utils/block-field-map/get-block-form-field-names';
import { getBlockFormValueEntries } from 'utils/block-field-map/get-block-form-value-entries';
import {
  CoapplicantCompatibilityItem,
  normalizeCoApplicantFieldName,
  normalizeCoapplicantListItem,
} from 'utils/coapplicantCompatibility';
import { isNonEmpty } from 'utils/display-logic/isNonEmpty';
import { validateSecondaryApplicantDraft } from 'utils/validateSecondaryApplicantDraft';

type PageResponse = Record<string, unknown>;
interface SecondaryApplicantDraft extends Record<string, unknown> {
  uid?: string;
  responseId?: string;
}
interface ApplicantIdentity {
  firstName: string;
  middleName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  birthDate: string;
  customerRelationshipId: number;
}
const pendingSecondaryApplicantSaves = new WeakMap<object, Promise<PageResponse[]>>();

export function getSecondaryApplicantSaveErrors(
  errors: FieldErrors<FieldValues>,
  getFieldState: UseFormGetFieldState<FieldValues>,
): FieldErrors<FieldValues> {
  const latestErrors = { ...errors };
  ['coApplicant', 'guarantor'].forEach((blockName) => {
    const blockErrors = getFieldState(blockName).error;
    if (blockErrors) latestErrors[blockName] = blockErrors;
    else delete latestErrors[blockName];
  });
  return latestErrors;
}

function getApplicantIdentity(
  draft: SecondaryApplicantDraft,
  isGuarantor: boolean,
): ApplicantIdentity {
  if (isGuarantor)
    return {
      firstName: String(draft.guarantor_first_name ?? ''),
      middleName: String(draft.guarantor_middle_name ?? ''),
      lastName: String(draft.guarantor_last_name ?? ''),
      email: String(draft.email ?? ''),
      phoneNumber: String(draft.contact_phone_number ?? ''),
      birthDate: String(draft.dob ?? ''),
      customerRelationshipId: Number(draft.relationship),
    };
  const coApplicant = normalizeCoapplicantListItem(
    draft as unknown as CoapplicantCompatibilityItem,
  );
  return {
    firstName: coApplicant.coapplicant_first_name,
    middleName: coApplicant.coapplicant_middle_name,
    lastName: coApplicant.coapplicant_last_name,
    email: coApplicant.coapplicant_email,
    phoneNumber: coApplicant.coapplicant_phone_number,
    birthDate: coApplicant.coapplicant_date_of_birth,
    customerRelationshipId: Number(coApplicant.relationship),
  };
}
const normalizeIdentityPart = (value: string): string => value.trim().toLowerCase();
function matchesExistingApplicant(existing: ApplicantIdentity, draft: ApplicantIdentity): boolean {
  if (
    existing.customerRelationshipId !== draft.customerRelationshipId ||
    normalizeIdentityPart(existing.firstName) !== normalizeIdentityPart(draft.firstName) ||
    normalizeIdentityPart(existing.lastName) !== normalizeIdentityPart(draft.lastName)
  )
    return false;
  if (draft.email.trim())
    return normalizeIdentityPart(existing.email) === normalizeIdentityPart(draft.email);
  return Boolean(
    (draft.phoneNumber || draft.birthDate) &&
      normalizeIdentityPart(existing.phoneNumber) === normalizeIdentityPart(draft.phoneNumber) &&
      normalizeIdentityPart(existing.birthDate) === normalizeIdentityPart(draft.birthDate),
  );
}

export function useSyncSecondaryApplicantsForPageSave() {
  const { control, getValues, setValue, setError, trigger } = useFormContext();
  const { minAge: coApplicantMinAge } = useApplicationSettings(RelationshipType.Coapplicant);
  const { minAge: guarantorMinAge } = useApplicationSettings(RelationshipType.Guarantor);
  const { tSystem } = useSystemTranslation();
  const { applicant, activeResponse, form } = useSelector((state: RootState) => state.application);
  const { isPreviewMode, isLeadProfile } = useSelector((state: RootState) => state.auth);
  const evaluateDisplayCondition = useEvaluateDisplayCondition();

  return async function syncSecondaryApplicants(
    pageResponses: PageResponse[],
    pageId: string,
    onValidated?: () => void,
  ): Promise<PageResponse[]> {
    if (isPreviewMode) {
      onValidated?.();
      return pageResponses;
    }
    const page = form.pages.find(({ id }) => id === pageId);
    const visibleBlocks =
      page?.sections
        ?.filter(
          (section) =>
            isLeadProfile ||
            evaluateDisplayCondition(section.displayLogic) !== ConditionalElementState.Hidden,
        )
        .flatMap((section) => section.elements ?? []) ?? [];
    const synchronizedResponses = pageResponses.map((response) => ({ ...response }));
    const batches = [
      {
        blockName: 'coApplicant',
        listName: 'coApplicantList',
        blockType: BlockType.Coapplicant,
        isGuarantor: false,
        minAge: coApplicantMinAge,
      },
      {
        blockName: 'guarantor',
        listName: 'guarantorList',
        blockType: BlockType.Guarantor,
        isGuarantor: true,
        minAge: guarantorMinAge,
      },
    ].flatMap((configuration) => {
      const block = visibleBlocks.find(
        (element): element is CoapplicantField | GuarantorField =>
          element.customType === configuration.blockType &&
          (isLeadProfile ||
            evaluateDisplayCondition(element.displayLogic) !== ConditionalElementState.Hidden),
      );
      const responseIndex = pageResponses.findIndex((response) =>
        Object.prototype.hasOwnProperty.call(response, configuration.blockName),
      );
      const blockResponse = pageResponses[responseIndex]?.[configuration.blockName] as
        | Record<string, unknown>
        | undefined;
      const savedRows = blockResponse?.[configuration.listName];
      if (
        !block ||
        !Array.isArray(savedRows) ||
        !savedRows.some((draft: SecondaryApplicantDraft) => !draft.responseId)
      )
        return [];
      const isHidden =
        evaluateDisplayCondition(block.displayLogic) === ConditionalElementState.Hidden ||
        page?.sections?.some(
          (section) =>
            section.elements?.includes(block) &&
            evaluateDisplayCondition(section.displayLogic) === ConditionalElementState.Hidden,
        );
      if (
        isLeadProfile &&
        isHidden &&
        !isNonEmpty(
          getBlockFormValueEntries(
            configuration.blockName,
            blockResponse,
            getBlockFormFieldNames(block),
          ).map(({ value }) => value),
        )
      )
        return [];
      const drafts = savedRows.map((draft: SecondaryApplicantDraft) => ({ ...draft }));
      return [
        {
          ...configuration,
          responseIndex,
          blockResponse,
          drafts,
          fields: block.configuration?.fields,
          isBirthDateEnabled:
            configuration.isGuarantor ||
            block.configuration?.fields?.some(
              (field) =>
                field.enabled &&
                normalizeCoApplicantFieldName(field.name) === CoApplicantFields.dateOfBirth.name,
            ),
          // Validate while fields are mounted, before another save can finish.
          validation: Promise.all(
            drafts.map((draft, index) =>
              draft.responseId
                ? true
                : trigger(`${configuration.blockName}.${configuration.listName}.${index}`),
            ),
          ),
        },
      ];
    });
    if (!batches.length) {
      onValidated?.();
      return pageResponses;
    }
    const previousSave = pendingSecondaryApplicantSaves.get(control);
    const pendingSave = Promise.resolve(previousSave)
      .catch(() => undefined)
      .then(synchronize);
    pendingSecondaryApplicantSaves.set(control, pendingSave);
    try {
      return await pendingSave;
    } finally {
      if (pendingSecondaryApplicantSaves.get(control) === pendingSave)
        pendingSecondaryApplicantSaves.delete(control);
    }

    async function synchronize(): Promise<PageResponse[]> {
      const batchValidations = await Promise.all(batches.map((batch) => batch.validation));
      onValidated?.();
      if (!batchValidations.some((validation) => validation.some(Boolean)))
        return synchronizedResponses;
      const orgId = Number(applicant.org?.id);
      const propertyId = Number(applicant.propertyId);
      const { applicationId, responseId: activeResponseId } = activeResponse;
      if (!orgId || !propertyId || !applicationId || !activeResponseId) {
        throw new Error(tSystem(SystemTranslationKeys.validation.applicationStepUnavailable));
      }
      // Restore the response being edited; the API resolves the primary for applicant operations.
      await getResponse(activeResponseId);
      const relationships: { id: number; customer_type_id?: string | number }[] = batches.some(
        (batch) => !batch.isGuarantor,
      )
        ? (await getCustomerRelationships()).data
        : [];
      for (let batchIndex = 0; batchIndex < batches.length; batchIndex += 1) {
        const batch = batches[batchIndex];
        const { blockName, listName, drafts, isGuarantor } = batch;
        const validRows = batchValidations[batchIndex];
        let existingRows: SecondaryApplicantDraft[];
        if (isGuarantor) {
          // eslint-disable-next-line no-await-in-loop
          existingRows = (await getGuarantors()).data.map((guarantor: SecondaryApplicantDraft) => ({
            ...guarantor,
            responseId: guarantor.uid,
          }));
        } else {
          // eslint-disable-next-line no-await-in-loop
          existingRows = (await getCoApplicants()).data.map((coApplicant) => ({ ...coApplicant }));
        }
        const claimedResponseIds = new Set(drafts.map((draft) => draft.responseId).filter(Boolean));
        for (let draftIndex = 0; draftIndex < drafts.length; draftIndex += 1) {
          const draft = drafts[draftIndex];
          const identity = getApplicantIdentity(draft, isGuarantor);
          const liveRows = (getValues(`${blockName}.${listName}`) ??
            []) as SecondaryApplicantDraft[];
          const liveDraft = draft.uid
            ? liveRows.find((candidate) => candidate.uid === draft.uid)
            : liveRows[draftIndex];
          if (liveDraft?.responseId) {
            draft.responseId = liveDraft.responseId;
            claimedResponseIds.add(liveDraft.responseId);
          }
          if (draft.responseId || !validRows[draftIndex]) {
            // eslint-disable-next-line no-continue
            continue;
          }
          const existing = existingRows.find(
            (candidate) =>
              !claimedResponseIds.has(candidate.responseId) &&
              matchesExistingApplicant(getApplicantIdentity(candidate, isGuarantor), identity),
          );
          if (existing?.responseId) {
            draft.responseId = existing.responseId;
            setValue(`${blockName}.${listName}.${draftIndex}.responseId`, existing.responseId);
            claimedResponseIds.add(existing.responseId);
            // eslint-disable-next-line no-continue
            continue;
          }
          const selectedRelationship = relationships.find(
            (relationship) => relationship.id === identity.customerRelationshipId,
          );
          const customerTypeId = selectedRelationship?.customer_type_id?.toString();
          const isAdult = isGuarantor || customerTypeId !== CustomerType.NotResponsible;
          const requiresEmail = isGuarantor || customerTypeId !== CustomerType.NonLeasingOccupant;
          // Options can also be saved from the cart while this page is unmounted.
          // eslint-disable-next-line no-await-in-loop
          const draftErrors = await validateSecondaryApplicantDraft(draft, {
            isGuarantor,
            isAdult,
            requiresEmail,
            isBirthDateEnabled: Boolean(batch.isBirthDateEnabled),
            minAge: batch.minAge,
            fields: batch.fields,
          });
          if (Object.keys(draftErrors).length) {
            Object.entries(draftErrors).forEach(([fieldName, error]) =>
              setError(`${blockName}.${listName}.${draftIndex}.${fieldName}`, error),
            );
            // eslint-disable-next-line no-continue
            continue;
          }
          let relationshipType = RelationshipType.Coapplicant;
          if (isGuarantor) relationshipType = RelationshipType.Guarantor;
          else if (selectedRelationship?.customer_type_id !== undefined) {
            relationshipType = mapEntrataCustomerTypeToRelationshipType(
              selectedRelationship.customer_type_id.toString() as CustomerType,
            );
          }
          const { customerRelationshipId, ...signupApplicant } = identity;
          // eslint-disable-next-line no-await-in-loop
          const secondaryResponse = await signupSecondaryApplicant({
            applicationId,
            orgId,
            propertyId,
            relationshipType,
            customerRelationshipId,
            primaryApplicationResponseId: activeResponseId,
            applicant: signupApplicant,
          });
          const secondaryResponseId = secondaryResponse.data.applicant.currentResponse.responseId;
          draft.responseId = secondaryResponseId;
          setValue(`${blockName}.${listName}.${draftIndex}.responseId`, secondaryResponseId);
          claimedResponseIds.add(secondaryResponseId);
          // eslint-disable-next-line no-await-in-loop
          await getResponse(activeResponseId);
        }
        synchronizedResponses[batch.responseIndex][blockName] = {
          ...batch.blockResponse,
          [listName]: drafts,
        };
      }
      return synchronizedResponses;
    }
  };
}
